import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { useDynamicModuleLoader } from 'shared/store'

import { Cart } from './types'

export interface CartSlice {
  cartList: Cart[]
  cartById: Cart | null
  totalProductsCount: number
}

const initialState: CartSlice = {
  cartList: [],
  cartById: null,
  totalProductsCount: 0,
}

export const cartSlice = createSlice({
  name: 'cartSlice',
  initialState,
  reducers: {
    setCartList: (state, action: PayloadAction<Cart[]>) => {
      state.cartList = action.payload
      state.totalProductsCount = action.payload.reduce(
        (acc, cart) => acc + cart.products_quantity,
        0,
      )
    },
    setCartById: (state, action: PayloadAction<Cart | null>) => {
      if (!action.payload?.order_products.length) {
        state.cartById = null
      } else {
        state.cartById = action.payload
      }
    },

    removeProduct: (state, action: PayloadAction<Cart>) => {
      const updatedCart = action.payload
      state.cartById = updatedCart

      if (updatedCart.order_products.length === 0) {
        state.cartList = state.cartList.filter(
          cart => cart.pk !== updatedCart.pk,
        )
      } else {
        state.cartList = state.cartList.map(cart =>
          cart.pk === updatedCart.pk ? updatedCart : cart,
        )
      }

      state.totalProductsCount = state.cartList.reduce(
        (acc, cart) => acc + cart.order_products_count,
        0,
      )
    },

    removeCart: (state, action: PayloadAction<Cart['pk']>) => {
      state.cartById = null
      state.cartList = state.cartList.filter(i => i.pk !== action.payload)
      state.totalProductsCount = state.cartList.reduce(
        (acc, cart) => acc + cart.order_products_count,
        0,
      )
    },

    mutateProduct: (state, action: PayloadAction<Cart>) => {
      const exist = state.cartList.find(i => i.pk === action.payload.pk) as Cart
      if (exist) {
        state.cartList = state.cartList.map(i =>
          i.pk === exist.pk ? action.payload : i,
        )
      } else {
        state.cartList = [...state.cartList, action.payload]
      }
      state.cartById = action.payload
      state.totalProductsCount = state.cartList.reduce(
        (acc, cart) => acc + cart.order_products_count,
        0,
      )
    },

    clearAllStates: state => {
      state.cartById = null
      state.cartList = []
      state.totalProductsCount = 0
    },
  },
})

export const CartSliceActions = cartSlice.actions

export const CartSliceReducer = cartSlice.reducer

export const CartSliceReducerState = {
  cart: CartSliceReducer,
}

export interface CartSliceSchema {
  cart?: CartSlice
}

export const useCartSlice = () =>
  useDynamicModuleLoader({
    reducers: { ...CartSliceReducerState },
    removeAfterUnmount: false,
  })
